// hooks/usePageTracking.ts
//
// The document title and ONE analytics page view per route (ROADMAP #62 P7 — only a new PATH counts: `isNewPageView`).
// ⓘ The full URL is handed over; the provider strips what may not leave (`safePageLocation`).
import { useEffect, useRef } from "react";
import siteMap, { SiteMapItem } from "@/config/siteMap";
import { useBranding } from "@/contexts/BrandingContext";
import { analytics, isNewPageView } from "@/utils";

// Convert siteMap URL pattern to regex (handles :param segments)
const urlToRegex = (url: string): RegExp => {
  // First replace :param with a placeholder
  // Then escape special regex chars
  // Then restore the placeholder as regex pattern
  const PLACEHOLDER = "___PARAM___";

  let pattern = url.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, PLACEHOLDER);
  pattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  pattern = pattern.replaceAll(PLACEHOLDER, "[^/]+");

  return new RegExp(`^${pattern}/?$`); // Allow optional trailing slash
};

// Recursively find matching siteMap item
const findSiteMapItem = (
  pathname: string,
  items: SiteMapItem[]
): SiteMapItem | null => {
  for (const item of items) {
    // Check nested items first (more specific routes)
    if (item.items?.length) {
      const found = findSiteMapItem(pathname, item.items);
      if (found) return found;
    }
    // Then check current item
    if (item.url && urlToRegex(item.url).test(pathname)) {
      return item;
    }
  }
  return null;
};

// A2e #101 — "<page> | <brand>": the brand comes from the white label (`useBranding`), never a name written here.
const titleOf = (item: SiteMapItem | null, brandName: string): string => (item?.name ? `${item.name} | ${brandName}` : brandName);

export const usePageTracking = () => {
  // ⚠ A ref, not a variable inside the effect: React's dev double-run of effects would reset a local one and count every
  //   route twice (seen in the console, 2026-10-01).
  const lastPathRef = useRef<string | null>(null);
  const { brand } = useBranding();
  // Read through a ref by the route listeners (installed once), so a page view after the white label loads is titled
  // with the loaded brand.
  const brandNameRef = useRef(brand.name);
  brandNameRef.current = brand.name;
  // The title THIS hook last set — a page that titled itself (a print sheet: its title becomes the PDF's name) is left
  // alone by the re-title below.
  const lastTitleRef = useRef<string | null>(null);

  useEffect(() => {
    const trackPageView = () => {
      const pathname = window.location.pathname;
      const item = findSiteMapItem(pathname, siteMap);

      // Update document title
      const title = titleOf(item, brandNameRef.current);
      document.title = title;
      lastTitleRef.current = title;

      if (!isNewPageView(lastPathRef.current, pathname)) {
        return;
      }

      lastPathRef.current = pathname;
      analytics.pageView(window.location.href, title, item?.url ?? null);
    };

    trackPageView();

    window.addEventListener("popstate", trackPageView);

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      trackPageView();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      trackPageView();
    };

    return () => {
      window.removeEventListener("popstate", trackPageView);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // The white label arrives after the first paint: re-title the CURRENT page with it (not a page view).
  useEffect(() => {
    if (lastTitleRef.current !== null && document.title !== lastTitleRef.current) {
      return;
    }

    const title = titleOf(findSiteMapItem(window.location.pathname, siteMap), brand.name);
    document.title = title;
    lastTitleRef.current = title;
  }, [brand.name]);
};
